//@ts-nocheck
import { routingMixin } from "@jack-henry/web-component-router";
import { LitElement, css, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import {
  fixedEncodeURIComponent,
  getRequest,
  putRequest,
} from "../../utils/jha-fetch";
import { Apis } from "../../utils/api-constants";
import {
  applyInstitutionFieldErrors,
} from "../../utils/institution-validation-errors";
import {
  INSTITUTION_CONCURRENCY_USER_MESSAGE,
  isInstitutionConcurrencyError,
} from "../../utils/institution-concurrency";
import { requestInstitutionsListRefresh } from "../../utils/institution-list-refresh";
import "../../Components/Loader";
import "@banno/jha-wc/src/buttons/jha-button/jha-button";
import "@banno/jha-wc/src/forms/jha-form-text-input/jha-form-text-input";
import "@banno/jha-wc/src/forms/jha-form-row/jha-form-row";
import { decryptValue, showToastMessage } from "../../utils/app-utils";
import { getErrorMessage } from "../../utils/getErrorMessage";
import { StatusConstants } from "../../utils/apiStatusConstants";
import { EditInstitutionType } from "../../types/BusinessUnits/EditInstitutionType";
import {
  InstitutionType,
  normalizeInstitution,
} from "../../types/BusinessUnits/InstitutionType";
import { openJhftDialog } from "../../Components/JhftJhaDialog";
import Cookies from "js-cookie";
import { AppRouters } from "../../utils/app-router";
import { GlobalVariables, UserTypes } from "../../utils/constants";
import { isSessionBusinessUnitAllowed } from "../../utils/business-unit-session";
import { FeatureFlags } from "../../utils/launchdarkly-config";
import { launchDarkly } from "../../utils/launchdarkly-service";
import { isInitialInstitutionFeatureEnabled } from "../../utils/initial-institution-feature-gate";

@customElement("edit-institution")
export class EditInstitution extends routingMixin(LitElement) {
  static get is() {
    return "edit-institution";
  }

  @property({ reflect: true, attribute: "business-unit-id" })
  businessUnitId = "";

  @property({ reflect: true, attribute: "institution-uuid" })
  institutionUuid = "";

  @property({ type: String }) displayInstitutionUuid = "";
  @property({ type: Boolean }) displayIsDefault = false;

  @property({ type: Object }) originalFormData: EditInstitutionType = {
    institutionRtId: "",
    institutionEnvironment: "",
    consumerNameProduct: "",
    updatedBy: decryptValue(Cookies.get("userId") || ""),
    eTag: "",
  };

  @property({ type: Object }) newFormData: EditInstitutionType = {
    institutionRtId: "",
    institutionEnvironment: "",
    consumerNameProduct: "",
    updatedBy: decryptValue(Cookies.get("userId") || ""),
    eTag: "",
  };

  @property({ type: Boolean }) apiLoading = false;
  @property({ type: Boolean }) dataNeedsSaving = false;
  @property({ type: Boolean }) isReadOnlyUser = /readonly/i.test(
    decryptValue(Cookies.get("userRole") || "")
  );
  @property({ type: Boolean }) isEnterpriseAdmin = /enterpriseadmin/i.test(
    decryptValue(Cookies.get("userType") || "")
  );
  @property({ type: String })
  loggedUserType = decryptValue(Cookies.get("userType") as string);
  @property({ type: String }) businessUnitName = "";
  @property({ type: String }) returnFilterEnvironment = "";
  @property({ type: String }) returnFilterDefault = "";

  private institutionLoadKey = "";

  get canManageInstitutions(): boolean {
    if (this.isReadOnlyUser) {
      return false;
    }
    if (this.isEnterpriseAdmin) {
      return true;
    }
    return this.loggedUserType?.toLowerCase() === UserTypes.businessadmin;
  }

  private handleInitialInstitutionFlagChange = (): void => {
    if (!isInitialInstitutionFeatureEnabled()) {
      if (this.apiLoading) {
        return;
      }
      showToastMessage(
        "Institution management is not available.",
        "danger"
      );
      AppRouters.go(this.institutionsListUrl());
      return;
    }
    this.requestUpdate();
  };

  private refreshFlagValues = (): void => {
    this.requestUpdate();
  };

  static readonly styles = css`
    #btnCon {
      display: flex;
      flex-direction: row;
      gap: 15px;
    }
    #editInstitution {
      padding: 24px;
      height: -webkit-fill-available;
      display: flex;
      flex-direction: column;
    }
    #breadcrumb {
      font-size: 14px;
      color: var(--jha-color-secondary-text);
      margin-bottom: 8px;
    }
    #breadcrumb a {
      color: var(--jha-color-primary);
      text-decoration: none;
      cursor: pointer;
    }
    #breadcrumb a:hover {
      text-decoration: underline;
    }
    h2 {
      margin-bottom: 18px;
      font-size: 24px;
      font-weight: 600;
    }
    @media (prefers-color-scheme: dark) {
      h2 {
        color: #a3d4ff;
      }
    }
    @media (prefers-color-scheme: light) {
      h2 {
        color: #06185f;
      }
    }
    jha-form-text-input {
      max-width: 410px;
    }
    .default-badge {
      display: inline-block;
      padding: 2px 10px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;
      background-color: var(--jha-color-success-light, #d4edda);
      color: var(--jha-color-success, #155724);
    }
    .default-readonly {
      margin-top: 8px;
      font-size: 14px;
    }
  `;

  private institutionsListUrl(): string {
    let url = `/business-units/${fixedEncodeURIComponent(
      this.businessUnitId
    )}/institutions`;
    const params: string[] = [];
    if (this.returnFilterEnvironment) {
      params.push(
        `environment=${encodeURIComponent(this.returnFilterEnvironment)}`
      );
    }
    if (this.returnFilterDefault) {
      params.push(
        `defaultStatus=${encodeURIComponent(this.returnFilterDefault)}`
      );
    }
    if (params.length) {
      url += `?${params.join("&")}`;
    }
    return url;
  }

  institutionDataChange() {
    const rtIdInput = this.shadowRoot?.querySelector(
      "jha-form-text-input[data-auto-id='institutionRtIdInput']"
    ) as HTMLInputElement;
    const environmentInput = this.shadowRoot?.querySelector(
      "jha-form-text-input[data-auto-id='institutionEnvironmentInput']"
    ) as HTMLInputElement;
    const consumerInput = this.shadowRoot?.querySelector(
      "jha-form-text-input[data-auto-id='consumerNameProductInput']"
    ) as HTMLInputElement;

    if (rtIdInput) {
      this.newFormData.institutionRtId = rtIdInput.value;
    }
    if (environmentInput) {
      this.newFormData.institutionEnvironment = environmentInput.value;
    }
    if (consumerInput) {
      this.newFormData.consumerNameProduct = consumerInput.value;
    }

    this.dataNeedsSaving = !this.compareOriginalIsSameAsNew(this.newFormData);
    this.updateSubmitButtonState();
  }

  private updateSubmitButtonState() {
    const submitButton = this.shadowRoot?.querySelector(
      "jha-button[data-auto-id='editInstitutionSaveButton']"
    ) as HTMLButtonElement;

    if (this.apiLoading) {
      submitButton?.setAttribute("disabled", "");
    } else if (
      this.isFormComplete() &&
      !this.compareOriginalIsSameAsNew(this.newFormData)
    ) {
      submitButton?.removeAttribute("disabled");
    } else {
      submitButton?.setAttribute("disabled", "");
    }
  }

  isFormComplete(): boolean {
    return (
      this.newFormData.institutionRtId.trim() !== "" &&
      this.newFormData.institutionEnvironment.trim() !== "" &&
      this.newFormData.consumerNameProduct.trim() !== ""
    );
  }

  compareOriginalIsSameAsNew(newFormData: EditInstitutionType): boolean {
    return (
      this.originalFormData.institutionRtId.trim() ===
      newFormData.institutionRtId.trim() &&
      this.originalFormData.institutionEnvironment.trim() ===
      newFormData.institutionEnvironment.trim() &&
      this.originalFormData.consumerNameProduct.trim() ===
      newFormData.consumerNameProduct.trim()
    );
  }

  setSubmitButtonLoading(loading: boolean) {
    const saveButton = this.shadowRoot?.querySelector(
      "jha-button[data-auto-id='editInstitutionSaveButton']"
    ) as HTMLButtonElement;
    if (loading) {
      saveButton?.setAttribute("isLoading", "");
    } else {
      saveButton?.removeAttribute("isLoading");
    }
  }

  clearButtonLoading() {
    this.setSubmitButtonLoading(false);
    const cancelButton = this.shadowRoot?.querySelector(
      "jha-button[data-auto-id='editInstitutionCancelButton']"
    ) as HTMLButtonElement;
    cancelButton?.removeAttribute("isLoading");
  }

  private hasAppliedFieldErrors(): boolean {
    if (!this.shadowRoot) {
      return false;
    }
    const staticWarnings = new Set([
      "RT ID is a required field.",
      "Environment is a required field.",
      "Consumer Name / Product is a required field.",
    ]);
    for (const el of this.shadowRoot.querySelectorAll(
      "jha-form-text-input[warning]"
    )) {
      const warning = el.getAttribute("warning") ?? "";
      if (warning && !staticWarnings.has(warning)) {
        return true;
      }
    }
    return false;
  }

  private async handleValidationErrorResponse(
    details: Array<Record<string, unknown>>
  ): Promise<void> {
    if (isInstitutionConcurrencyError(details)) {
      showToastMessage(INSTITUTION_CONCURRENCY_USER_MESSAGE, "danger");
      await this.loadInstitutionForEdit();
      return;
    }

    let unmapped = "";
    if (this.shadowRoot) {
      unmapped = applyInstitutionFieldErrors(this.shadowRoot, details);
    } else {
      unmapped = getErrorMessage(details);
    }

    if (unmapped) {
      showToastMessage(unmapped, "danger");
    } else if (!this.hasAppliedFieldErrors()) {
      showToastMessage("Something went wrong, please try again.", "danger");
    }
  }

  private async confirmNavigateAway(destination: string): Promise<void> {
    if (this.apiLoading) {
      return;
    }

    const leave = () => {
      AppRouters.go(destination);
    };

    // dataNeedsSaving is the same signal that disables Save until something changed.
    if (this.isReadOnlyUser || !this.dataNeedsSaving) {
      leave();
      return;
    }

    try {
      const result = await openJhftDialog({
        variant: "warning",
        title: "Unsaved Changes",
        message: "You have unsaved changes. Are you sure you want to leave without saving?",
        cancelLabel: "Stay",
        confirmLabel: "Leave Without Saving",
      });
      if (result === "confirm") {
        leave();
        return;
      }
    } finally {
      if (this.isConnected) {
        this.clearButtonLoading();
        this.apiLoading = false;
      }
    }
  }

  async onCancelClick() {
    await this.confirmNavigateAway(this.institutionsListUrl());
  }

  validateFormData(formData: EditInstitutionType): boolean {
    if (formData.institutionRtId.trim() === "") {
      showToastMessage("RT ID is a required field.", "danger");
      return false;
    }
    if (formData.institutionEnvironment.trim() === "") {
      showToastMessage("Environment is a required field.", "danger");
      return false;
    }
    if (formData.consumerNameProduct.trim() === "") {
      showToastMessage("Consumer Name / Product is a required field.", "danger");
      return false;
    }
    if (!formData.eTag) {
      showToastMessage("Institution version is missing. Reload and try again.", "danger");
      return false;
    }
    return true;
  }

  trimFormDataValues(formData: EditInstitutionType): EditInstitutionType {
    return {
      institutionRtId: formData.institutionRtId.trim(),
      institutionEnvironment: formData.institutionEnvironment.trim(),
      consumerNameProduct: formData.consumerNameProduct.trim(),
      updatedBy: formData.updatedBy
        ? formData.updatedBy.trim()
        : decryptValue(Cookies.get("userId") || ""),
      eTag: formData.eTag,
    };
  }

  private buildPutBody(
    trimmed: EditInstitutionType
  ): Pick<
    EditInstitutionType,
    "institutionRtId" | "institutionEnvironment" | "consumerNameProduct" | "updatedBy"
  > {
    return {
      institutionRtId: trimmed.institutionRtId,
      institutionEnvironment: trimmed.institutionEnvironment,
      consumerNameProduct: trimmed.consumerNameProduct,
      updatedBy: trimmed.updatedBy,
    };
  }

  async onSaveInstitutionClick() {
    if (!isInitialInstitutionFeatureEnabled()) {
      showToastMessage(
        "Institution management is not available.",
        "danger"
      );
      AppRouters.go(this.institutionsListUrl());
      return;
    }
    if (!this.canManageInstitutions) {
      showToastMessage("You do not have permission to edit institutions.", "danger");
      return;
    }
    if (!this.institutionUuid || !this.businessUnitId) {
      showToastMessage(
        "Missing institution or business unit identifier.",
        "danger"
      );
      return;
    }
    if (this.apiLoading) {
      return;
    }

    this.apiLoading = true;
    this.institutionDataChange();
    const trimmed = this.trimFormDataValues(this.newFormData);
    this.newFormData = trimmed;

    if (!this.validateFormData(trimmed)) {
      this.apiLoading = false;
      this.clearButtonLoading();
      return;
    }

    try {
      this.setSubmitButtonLoading(true);

      const url =
        Apis.INSTITUTION_UPDATE +
        `/${fixedEncodeURIComponent(this.institutionUuid)}` +
        `?businessUnitId=${fixedEncodeURIComponent(this.businessUnitId)}`;

      const response = await putRequest(
        url,
        trimmed.eTag,
        this.buildPutBody(trimmed)
      );

      if (!response) {
        return;
      }

      const httpStatus =
        response instanceof Response ? response.status : response?.status;
      if (httpStatus === StatusConstants.UNAUTHORIZED_USER_CODE) {
        showToastMessage(
          "You do not have permission to edit institutions.",
          "danger"
        );
        return;
      }
      if (httpStatus === StatusConstants.NOT_FOUND_USER_CODE) {
        showToastMessage("Institution not found.", "danger");
        return;
      }
      if (httpStatus === 412) {
        showToastMessage(INSTITUTION_CONCURRENCY_USER_MESSAGE, "danger");
        await this.loadInstitutionForEdit();
        return;
      }
      if (response instanceof Response) {
        showToastMessage("Something went wrong, please try again.", "danger");
        return;
      }

      if (response?.messageStatus === null) {
        showToastMessage("Institution updated successfully.", "success");
        requestInstitutionsListRefresh(this.businessUnitId);
        AppRouters.go(this.institutionsListUrl());
        return;
      }

      if (response?.details?.length > 0) {
        await this.handleValidationErrorResponse(response.details);
        return;
      }

      const errMsg = getErrorMessage(response?.details);
      showToastMessage(
        errMsg.trim() || "Something went wrong, please try again.",
        "danger"
      );
    } catch {
      showToastMessage("Something went wrong, please try again.", "danger");
    } finally {
      this.apiLoading = false;
      this.clearButtonLoading();
      this.updateSubmitButtonState();
    }
  }

  private async verifyBusinessUnitAccess(): Promise<boolean> {
    try {
      const buData = await getRequest(
        Apis.GET_BUSINESSUNIT +
        `/${fixedEncodeURIComponent(this.businessUnitId)}`
      );

      if (!buData) {
        AppRouters.go("/business-units");
        return false;
      }

      if (buData instanceof Response) {
        if (buData.status === StatusConstants.UNAUTHORIZED_USER_CODE) {
          showToastMessage(
            "You do not have permission to manage this Business Unit.",
            "danger"
          );
        } else if (buData.status === StatusConstants.NOT_FOUND_USER_CODE) {
          showToastMessage("Business Unit not found.", "danger");
        } else {
          showToastMessage("Something went wrong, please try again.", "danger");
        }
        AppRouters.go("/business-units");
        return false;
      }

      if (
        this.loggedUserType?.toLowerCase() === UserTypes.businessadmin &&
        !isSessionBusinessUnitAllowed(buData)
      ) {
        showToastMessage(
          "You do not have permission to manage this Business Unit.",
          "danger"
        );
        AppRouters.go("/business-units");
        return false;
      }

      this.businessUnitName =
        buData.businessUnitName ?? buData.BusinessUnitName ?? "";
      return true;
    } catch {
      showToastMessage("Something went wrong, please try again.", "danger");
      AppRouters.go("/business-units");
      return false;
    }
  }

  private async loadInstitutionForEdit(): Promise<boolean> {
    if (!this.institutionUuid || !this.businessUnitId) {
      showToastMessage(
        "Missing institution or business unit identifier.",
        "danger"
      );
      AppRouters.go(this.institutionsListUrl());
      return false;
    }

    this.apiLoading = true;
    const loadKey = `${this.businessUnitId}:${this.institutionUuid}`;
    this.institutionLoadKey = loadKey;
    try {
      const url =
        Apis.INSTITUTION_GET +
        `/${fixedEncodeURIComponent(this.institutionUuid)}` +
        `?businessUnitId=${fixedEncodeURIComponent(this.businessUnitId)}`;

      const response = await getRequest(url);

      if (loadKey !== this.institutionLoadKey) {
        return false;
      }

      if (!response) {
        AppRouters.go(this.institutionsListUrl());
        return false;
      }

      if (response instanceof Response && response.status === 404) {
        showToastMessage("Institution not found.", "danger");
        AppRouters.go(this.institutionsListUrl());
        return false;
      }

      if (response instanceof Response && response.status === 401) {
        showToastMessage(
          "You do not have permission to edit this institution.",
          "danger"
        );
        AppRouters.go(this.institutionsListUrl());
        return false;
      }

      if (response.messageStatus === null) {
        const inst: InstitutionType = normalizeInstitution(response);
        if (
          !inst.institutionUuid ||
          !inst.businessUnitId ||
          inst.businessUnitId !== this.businessUnitId
        ) {
          showToastMessage("Institution not found.", "danger");
          AppRouters.go(this.institutionsListUrl());
          return false;
        }

        this.displayInstitutionUuid = inst.institutionUuid;
        this.displayIsDefault = inst.isDefault;

        const formState: EditInstitutionType = {
          institutionRtId: inst.institutionRtId,
          institutionEnvironment: inst.institutionEnvironment,
          consumerNameProduct: inst.consumerNameProduct,
          updatedBy: decryptValue(Cookies.get("userId") || ""),
          eTag: inst.eTag ?? "",
        };
        if (!formState.eTag) {
          showToastMessage(
            "Institution version is missing. Reload and try again.",
            "danger"
          );
          AppRouters.go(this.institutionsListUrl());
          return false;
        }
        this.originalFormData = { ...formState };
        this.newFormData = { ...formState };
        this.dataNeedsSaving = false;
        return true;
      }

      if (response.details?.length > 0) {
        showToastMessage(getErrorMessage(response.details), "danger");
      } else {
        showToastMessage(
          "Something went wrong loading institution details, please try again.",
          "danger"
        );
      }
      AppRouters.go(this.institutionsListUrl());
      return false;
    } catch {
      showToastMessage(
        "Something went wrong loading institution details, please try again.",
        "danger"
      );
      AppRouters.go(this.institutionsListUrl());
      return false;
    } finally {
      if (loadKey === this.institutionLoadKey) {
        this.apiLoading = false;
      }
    }
  }

  async routeEnter(
    currentNode: unknown,
    nextNodeIfExists: unknown,
    routeId: string,
    context: unknown
  ) {
    await super.routeEnter(currentNode, nextNodeIfExists, routeId, context);
    if (!nextNodeIfExists) {
      const params = new URLSearchParams(window.location.search);
      this.returnFilterEnvironment = params.get("environment") ?? "";
      const defaultParam = params.get("defaultStatus") ?? "";
      this.returnFilterDefault =
        defaultParam === "true" || defaultParam === "false"
          ? defaultParam
          : "";

      await launchDarkly.whenReady();
      if (!isInitialInstitutionFeatureEnabled()) {
        showToastMessage(
          "Institution management is not available.",
          "danger"
        );
        AppRouters.go(this.institutionsListUrl());
        return;
      }

      if (!this.canManageInstitutions) {
        showToastMessage(
          "You do not have permission to edit institutions.",
          "danger"
        );
        AppRouters.go(this.institutionsListUrl());
        return;
      }

      const allowed = await this.verifyBusinessUnitAccess();
      if (!allowed) {
        return;
      }

      const loaded = await this.loadInstitutionForEdit();
      if (!loaded) {
        return;
      }
      await this.updateComplete;
      this.updateSubmitButtonState();
    }
  }

  async connectedCallback() {
    super.connectedCallback();
    this.refreshFlagValues();
    launchDarkly.whenReady().then(() => this.refreshFlagValues());
    launchDarkly.on(FeatureFlags.INITIAL_INSTITUTION, this.handleInitialInstitutionFlagChange);
    document.title = GlobalVariables.MAIN_TITLE?.concat(
      " > Edit Institution"
    );
  }

  disconnectedCallback() {
    launchDarkly.off(FeatureFlags.INITIAL_INSTITUTION, this.handleInitialInstitutionFlagChange);
    super.disconnectedCallback();
  }

  onBackToInstitutions() {
    this.confirmNavigateAway(this.institutionsListUrl());
  }

  render() {
    return html`
      ${this.apiLoading ? html`<circle-loader></circle-loader>` : undefined}
      <div id="editInstitution">
        <div id="breadcrumb">
          <a @click=${this.onBackToBusinessUnits}>Business Units</a>
          &rsaquo;
          <a @click=${this.onBackToInstitutions}
            >${this.businessUnitName || this.businessUnitId}</a
          >
          &rsaquo; Institutions &rsaquo; Edit
        </div>
        <h1>Edit Institution</h1>

        <div>
          <h2>Institution Information</h2>
          <div style="max-width: 410px;">
            <jha-form-row>
              <jha-form-text-input
                data-auto-id="institutionUuidInput"
                label="Institution UUID"
                outline
                readonly
                .value=${this.displayInstitutionUuid}
              >
              </jha-form-text-input>
            </jha-form-row>
            <jha-form-row>
              <jha-form-text-input
                data-auto-id="institutionRtIdInput"
                label="RT ID"
                required
                outline
                minlength="1"
                maxlength="256"
                warning="RT ID is a required field."
                .value=${this.newFormData.institutionRtId}
                ?disabled=${this.apiLoading}
                @change=${this.institutionDataChange}
              >
              </jha-form-text-input>
            </jha-form-row>
            <jha-form-row>
              <jha-form-text-input
                data-auto-id="institutionEnvironmentInput"
                label="Environment"
                required
                outline
                minlength="1"
                maxlength="256"
                warning="Environment is a required field."
                .value=${this.newFormData.institutionEnvironment}
                ?disabled=${this.apiLoading}
                @change=${this.institutionDataChange}
              >
              </jha-form-text-input>
            </jha-form-row>
            <jha-form-row>
              <jha-form-text-input
                data-auto-id="consumerNameProductInput"
                label="Consumer Name / Product"
                required
                outline
                minlength="1"
                maxlength="256"
                warning="Consumer Name / Product is a required field."
                .value=${this.newFormData.consumerNameProduct}
                ?disabled=${this.apiLoading}
                @change=${this.institutionDataChange}
              >
              </jha-form-text-input>
            </jha-form-row>
            <div class="default-readonly" data-auto-id="institutionDefaultReadonly">
              <span class="field-label">Default institution</span>
              <div>
                ${this.displayIsDefault
        ? html`<span class="default-badge">Default</span>`
        : html`No`}
              </div>
            </div>
          </div>
        </div>

        <div style="flex:1;"></div>
        <div id="btnCon">
          <jha-button
            data-auto-id="editInstitutionSaveButton"
            type="button"
            large
            ?disabled=${this.apiLoading ||
      !this.isFormComplete() ||
      this.compareOriginalIsSameAsNew(this.newFormData)}
            @click=${this.onSaveInstitutionClick}
          >
            Save Changes
          </jha-button>
          <jha-button
            data-auto-id="editInstitutionCancelButton"
            type="button"
            tertiary
            large
            ?disabled=${this.apiLoading}
            @click=${this.onCancelClick}
          >
            Cancel
          </jha-button>
        </div>
      </div>
    `;
  }

  onBackToBusinessUnits() {
    this.confirmNavigateAway("/business-units");
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "edit-institution": EditInstitution;
  }
}
